/**
 * Countdown Timer Component
 * Replaces "countdown-timer-promotion" text with live countdown in elements marked with data-countdown="true"
 * Supports timezone-aware display and customizable formatting
 */

class CountdownTimer {
  constructor() {
    this.timers = new Map(); // Store multiple countdown instances
    this.intervalId = null;
    
    // Get theme settings from global window object
    this.settings = window.themeSettings?.countdown || {};
    
    // Default configuration
    this.config = {
      endDate: this.settings.endDate || '2024-12-31', // Simple date format
      format: this.settings.format || 'Ends in {days} Days {hours} Hours {minutes} Minutes {seconds} Seconds',
      textToReplace: 'countdown-timer-promotion'
    };
    
    // Only initialize if countdown elements exist
    if (document.querySelector('[data-countdown="true"]')) {
      this.init();
    }
  }

  init() {
    // Find and process all countdown elements
    this.processCountdownElements();
    
    // Start the global timer if we have any active countdowns
    if (this.timers.size > 0) {
      this.startTimer();
    }
  }

  processCountdownElements() {
    const elements = document.querySelectorAll('[data-countdown="true"]');
    
    elements.forEach((element, index) => {
      // Convert simple date format to full datetime (end of day in user's timezone)
      const endDate = this.config.endDate;
      const targetDateTime = `${endDate}T23:59:59`; // End of day
      
      // Create date object - will be in user's local timezone
      const target = new Date(targetDateTime);
      if (isNaN(target.getTime())) {
        console.warn('Invalid countdown end date:', endDate);
        return;
      }
      
      // Create unique ID for this countdown
      const countdownId = `countdown-${index}`;
      element.setAttribute('data-countdown-id', countdownId);
      
      // Store countdown configuration
      this.timers.set(countdownId, {
        element,
        target,
        format: this.config.format,
        expired: false
      });
      
      // Replace the placeholder text initially
      this.updateCountdownDisplay(countdownId);
    });
  }

  startTimer() {
    // Update every second
    this.intervalId = setInterval(() => {
      this.updateAllCountdowns();
    }, 1000);
    
    // Initial update
    this.updateAllCountdowns();
  }

  updateAllCountdowns() {
    let activeCountdowns = 0;
    
    this.timers.forEach((countdown, id) => {
      if (!countdown.expired) {
        this.updateCountdownDisplay(id);
        if (!countdown.expired) {
          activeCountdowns++;
        }
      }
    });
    
    // Stop timer if no active countdowns
    if (activeCountdowns === 0 && this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  updateCountdownDisplay(countdownId) {
    const countdown = this.timers.get(countdownId);
    if (!countdown || countdown.expired) return;

    const now = new Date();
    const timeLeft = countdown.target.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      this.handleExpiredCountdown(countdownId);
      return;
    }
    
    // Calculate time components
    const timeComponents = this.calculateTimeComponents(timeLeft);
    
    // Format the countdown text
    const countdownText = this.formatCountdownText(countdown.format, timeComponents);
    
    // Replace the placeholder text
    this.replaceCountdownText(countdown.element, countdownText);
  }

  calculateTimeComponents(milliseconds) {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    return {
      days: days,
      hours: hours % 24,
      minutes: minutes % 60,
      seconds: seconds % 60
    };
  }

  formatCountdownText(format, components) {
    let formattedText = format;
    
    // Handle days - hide if 0
    if (components.days === 0) {
      // Remove days and the word "Days" (with various patterns)
      formattedText = formattedText
        .replace(/\{days\}\s*Days?\s*/gi, '')
        .replace(/\{days\}\s*d\s*/gi, '')
        .replace(/\{days\}/gi, '');
    } else {
      formattedText = formattedText.replace('{days}', components.days.toString());
    }
    
    // Handle hours - hide if 0
    if (components.hours === 0) {
      // Remove hours and the word "Hours" (with various patterns)
      formattedText = formattedText
        .replace(/\{hours\}\s*Hours?\s*/gi, '')
        .replace(/\{hours\}\s*h\s*/gi, '')
        .replace(/\{hours\}/gi, '');
    } else {
      formattedText = formattedText.replace('{hours}', components.hours.toString().padStart(2, '0'));
    }
    
    // Always show minutes and seconds (even if 0)
    formattedText = formattedText
      .replace('{minutes}', components.minutes.toString().padStart(2, '0'))
      .replace('{seconds}', components.seconds.toString().padStart(2, '0'));
    
    // Clean up any double spaces or leading/trailing spaces
    formattedText = formattedText.replace(/\s+/g, ' ').trim();
    
    // Clean up any leading punctuation or separators
    formattedText = formattedText.replace(/^[:\-\s]+/, '');
    
    return formattedText;
  }

  replaceCountdownText(element, countdownText) {
    // Only replace if the element contains our placeholder text
    if (element.innerHTML.includes(this.config.textToReplace)) {
      // Create countdown HTML with matching styles
      const countdownHtml = `<span class="countdown-timer-display" style="font-family: inherit; font-size: inherit; font-weight: inherit; color: inherit;">${countdownText}</span>`;
      
      // Replace the placeholder text (works for both single and multiple instances)
      let updatedHTML = element.innerHTML;
      while (updatedHTML.includes(this.config.textToReplace)) {
        updatedHTML = updatedHTML.replace(this.config.textToReplace, countdownHtml);
      }
      element.innerHTML = updatedHTML;
    } else {
      // If already replaced, just update all countdown displays
      const displayElements = element.querySelectorAll('.countdown-timer-display');
      displayElements.forEach(displayElement => {
        displayElement.textContent = countdownText;
      });
    }
  }

  handleExpiredCountdown(countdownId) {
    const countdown = this.timers.get(countdownId);
    if (!countdown) return;
    
    countdown.expired = true;
    
    // Check if element should be hidden when expired
    const hideWhenExpired = countdown.element.dataset.countdownHideExpired === 'true';
    
    if (hideWhenExpired) {
      countdown.element.style.display = 'none';
    } else {
      // Replace with expired message
      const expiredText = countdown.element.dataset.countdownExpiredText || 'Offer has ended';
      this.replaceCountdownText(countdown.element, expiredText);
    }
    
    // Dispatch custom event for expired countdown
    countdown.element.dispatchEvent(new CustomEvent('countdownExpired', {
      detail: { countdownId, element: countdown.element }
    }));
  }

  // Public method to add new countdown dynamically
  addCountdown(element, targetDate, options = {}) {
    const countdownId = `countdown-dynamic-${Date.now()}`;
    element.setAttribute('data-countdown-id', countdownId);
    
    const target = new Date(targetDate);
    if (isNaN(target.getTime())) {
      console.warn('Invalid countdown target date:', targetDate);
      return null;
    }
    
    this.timers.set(countdownId, {
      element,
      target,
      format: options.format || this.config.format,
      timezoneMode: options.timezoneMode || this.config.timezoneMode,
      expired: false
    });
    
    // Start timer if not already running
    if (!this.intervalId) {
      this.startTimer();
    }
    
    return countdownId;
  }

  // Public method to remove countdown
  removeCountdown(countdownId) {
    if (this.timers.has(countdownId)) {
      this.timers.delete(countdownId);
    }
  }

  // Cleanup method
  destroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.timers.clear();
  }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Only initialize if countdown timer is enabled in theme settings
  if (window.themeSettings?.enableCountdownTimer) {
    window.countdownTimer = new CountdownTimer();
  }
});

// Handle dynamic content loading (for AJAX/Shopify sections)
document.addEventListener('shopify:section:load', () => {
  if (window.themeSettings?.enableCountdownTimer && window.countdownTimer) {
    // Re-process countdown elements in newly loaded sections
    window.countdownTimer.processCountdownElements();
  }
});
